/**
 * Room — the live lobby experience. Renders the seat grid (waiting),
 * the GameStage (playing), the PostRoundPane (roundEnd), and the
 * FinalStandings (finalStandings). The Lobby settings panel (table rules —
 * rounds/win-condition/game pool/etc., NOT app settings) is one tap away
 * via the "Table Rules" button, and again from the chalkboard status sheet.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Character, LobbyTicket, TavernConfigDTO } from '../../lib/api';
import { listGames, getCharacterStats, getCharacter, type GameTile } from '../../lib/api';
import { useLobbySocket, clearRejoin } from './useLobbySocket';
import { useJukeboxPlayback } from './useJukeboxPlayback';
import { getRecord } from '@tt/lobby/records';
import { computeOwnedRecordIds } from './recordOwnership';
import { JukeboxDrawer } from './JukeboxDrawer';
import { GameStage } from '../game/GameStage';
import { UniversalMatchIntro } from '../game/UniversalMatchIntro';
import { enterGameFullscreen, exitGameFullscreen, lockOrientation, onFullscreenChange, isFullscreen, isIos, fullscreenSupported } from '../game/fullscreen';
import { RotateOverlay } from '../game/RotateOverlay';
import { IosInstallNudge } from '../game/IosInstallNudge';
import { gameOrientation, gameName } from '../game/manifests';
import { SettingsPanel } from './SettingsPanel';
import { PostRoundPane } from './PostRoundPane';
import { MatchStartVotePane } from './MatchStartVotePane';
import { FinalStandings } from './FinalStandings';
import { FreeModeGrid } from './FreeModeGrid';
import { FreeModeStandings } from './FreeModeStandings';
import { gameAvailability } from './gameAvailability';
import { feedbackPrefs } from '@tt/feedback';
import { audio } from '@tt/audio';
import { MeDrawer } from '../me/MeDrawer';
import { XPBar } from '../me/XPBar';
import { BellButton } from '../me/BellButton';
import { DailyChallengeWidget } from '../me/DailyChallengeWidget';
import { ToastQueue, useToastQueue } from '../me/UnlockToast';
import { ScrollText, Volume2, VolumeX, Smile, Link2, LogOut, Flame, Star, Music, ChevronUp, ChevronDown } from 'lucide-react';
import { featuredGameForWeek, FEATURED_XP_MULTIPLIER } from '@tt/lobby/season';
import { TavernBackdrop } from './TavernBackdrop';
import { RoomBackdrop3D } from './RoomBackdrop3D';
import { DoorWalkTransition } from './DoorWalkTransition';
import { useHallWalk } from './HallWalkControls';
import { FirstVisitHints } from './FirstVisitHints';
import { FirstHostHint } from './FirstHostHint';
import { TavernFloor } from './TavernFloor';
import { RosterDrawer } from './RosterDrawer';
import { MAX_PLAYERS } from '@tt/players';
import { CharacterProfileModal } from '../characters/CharacterProfileModal';
import type { RoundXp } from '@tt/lobby';
import { track } from '../../lib/analytics';

interface Props {
  character: Character;
  ticket: LobbyTicket;
  onLeave: () => void;
  onCharacterUpdated: (character: Character) => void;
  initialSeq?: number;
}

const REACTION_PICKER_BASE = ['👏', '🔥', '💀', '🎉', '😂', '😱'];
const REACTION_PICKER_UNLOCKABLE = ['🏆', '⚡', '🎲', '💎', '🍀', '🎭'];

export function Room({ character, ticket, onLeave, onCharacterUpdated, initialSeq }: Props) {
  const sock = useLobbySocket({
    wsUrl: ticket.wsUrl,
    ticket: ticket.ticket,
    enabled: true,
    charId: character.charId,
    sinceSeq: initialSeq,
  });

  const [games, setGames] = useState<GameTile[]>([]);
  const [chatDraft, setChatDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openSettingsAfterReset, setOpenSettingsAfterReset] = useState(false);
  // Free-mode end-of-session summary. Free play has no server-side
  // finalStandings (no win condition), so ending a session used to just
  // kick everyone to the home screen. When the host ends the session we
  // synthesize a standings summary from the accumulated lobbyScores and
  // route it through the existing FinalStandings ceremony before leaving.
  const [freeEnd, setFreeEnd] = useState<{
    standings: Array<{ charId: string; name: string; points: number }>;
    awards: Array<{ charId: string; label: string }>;
    winnerHue: number;
  } | null>(null);
  const [reactionPicker, setReactionPicker] = useState(false);
  // Touch/mobile devices hide the in-game reaction picker: its floating button
  // overlapped key gameplay UI on phones (owner report). Reactions are still
  // sendable from the lobby TavernFloor; only the in-match button is dropped.
  const [isTouchDevice] = useState(() =>
    typeof window !== 'undefined' && (('ontouchstart' in window) || ((navigator.maxTouchPoints ?? 0) > 0)),
  );
  const [meOpen, setMeOpen] = useState(false);
  const [profileCharId, setProfileCharId] = useState<string | null>(null);
  /** Waiting Room 2.0: the chalkboard is a tap-to-open sheet now — the
   *  HALL is the screen; chrome lives at the edges (owner, 2026-07-04:
   *  "players can't hardly see other people, chalkboard covers most of
   *  the screen"). */
  const [boardOpen, setBoardOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(!audio.prefs.get().muted);
  const [unlockedEmotes, setUnlockedEmotes] = useState<string[]>([]);
  const [dailyStreak, setDailyStreak] = useState<number>(0);
  /** Phase D — lantern balance pip in the Room header. Initialized from
   *  the cached character row (so it shows immediately on first paint),
   *  refreshed from /stats once the initial fetch lands and again at
   *  every roundEnd / finalStandings transition (via the same stats
   *  refresh path the unlock toaster uses). */
  const [lanternBalance, setLanternBalance] = useState<number>(character.lanterns ?? 0);
  // "Your Own Tavern" Phase B2 — jukebox record ownership, refreshed
  // alongside the existing stats fetches below (no second network call).
  const [jukeboxOwnedIds, setJukeboxOwnedIds] = useState<Set<string>>(new Set());
  const [jukeboxOpen, setJukeboxOpen] = useState(false);
  // "Walk Through the Door" (Own Tavern §3) — the door-walk plays once per
  // match at the START of `playing`, then clears. Purely client-side
  // choreography over the already-live GameStage; bails to the plain fade
  // internally on reduced motion / WebGL failure / too-short a window, so
  // match entry is never delayed or blocked.
  //
  // LATCHED, not keyed on the live matchId: at match start the client can
  // briefly receive TWO game.start events (distinct matchIds bounce
  // A→B→A while lobby.state stays 'playing' — near-simultaneous vote
  // resolution / dual delivery lanes). Keying the overlay on the live
  // sock.currentMatch.matchId re-keyed it on every bounce → the walk
  // restarted. We instead latch the FIRST matchId seen entering 'playing'
  // and hold it (stable key) until the walk completes, so it mounts EXACTLY
  // once. (GameStage is separately keyed on the live matchId and still
  // re-mounts on the bounce — a pre-existing match-flow issue, reported.)
  const [doorWalk, setDoorWalk] = useState<{ matchId: string; startsAt: number } | null>(null);
  const [doorWalkDoneMatch, setDoorWalkDoneMatch] = useState<string | null>(null);
  useEffect(() => {
    if (sock.lobby?.state === 'playing' && sock.currentMatch) {
      // Latch on FIRST sight; ignore subsequent matchId bounces.
      setDoorWalk((prev) => prev ?? { matchId: sock.currentMatch!.matchId, startsAt: sock.currentMatch!.startsAt });
    } else if (sock.lobby?.state && sock.lobby.state !== 'voting') {
      // Left the match region (roundEnd / waiting / finalStandings) — disarm
      // so the next match re-arms. 'voting' is left alone: it precedes the
      // first match (already disarmed) and can appear as a transient blip.
      setDoorWalk(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sock.lobby?.state, sock.currentMatch?.matchId]);
  const [xpRefreshKey, setXpRefreshKey] = useState(0);
  const [xpBoost, setXpBoost] = useState(false);
  const [xpToast, setXpToast] = useState<{ matchId: string; xp: RoundXp } | null>(null);
  const toastQueue = useToastQueue();
  const lastToastedMatchRef = useRef<string | null>(null);
  const prevUnlockedRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    void (async () => {
      try {
        const s = await getCharacterStats(character.charId);
        setUnlockedEmotes(s.inventory.emotes ?? []);
        setDailyStreak(s.dailyStreak ?? 0);
        if (typeof s.lanterns === 'number') setLanternBalance(s.lanterns);
        setJukeboxOwnedIds(computeOwnedRecordIds(
          new Set(s.achievements.map((a) => a.code)),
          s.inventory as unknown as Record<string, unknown>,
        ));
        // Seed the unlocked-id snapshot so we don't toast existing inventory.
        prevUnlockedRef.current = collectUnlockedIds(s.inventory.unlockHints ?? []);
      } catch { /* */ }
    })();
  }, [character.charId]);

  const chatEndRef = useRef<HTMLDivElement | null>(null);

  // ---- Fullscreen takeover (Total-Redesign-Spec §1b, wave 0) ----
  // Entry happens inside the Ready/Start CLICK handlers below (transient
  // activation required); this block handles exit + orientation lock +
  // the ⛶ re-enter pill's "are we fullscreen?" state.
  const [fsActive, setFsActive] = useState<boolean>(() => isFullscreen());
  useEffect(() => onFullscreenChange(() => setFsActive(isFullscreen())), []);
  const lobbyState = sock.lobby?.state;
  const prevLobbyStateRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevLobbyStateRef.current;
    prevLobbyStateRef.current = lobbyState;
    // Leave takeover when a round ends (playing → anything else). We do
    // NOT exit on waiting→voting etc. so fullscreen entered at Ready
    // survives the vote screen into the match.
    if (prev === 'playing' && lobbyState !== 'playing') {
      void exitGameFullscreen();
    }
  }, [lobbyState]);
  // Landscape lock for orientation-declared games — only valid while
  // fullscreen, and needs no gesture, so it can run when the match starts.
  const playingGameId = lobbyState === 'playing' ? sock.currentMatch?.gameId : undefined;
  useEffect(() => {
    if (!playingGameId) return;
    const o = gameOrientation(playingGameId);
    if (o === 'landscape') void lockOrientation('landscape');
  }, [playingGameId]);
  // Unmount (user leaves mid-game): drop takeover.
  useEffect(() => () => { void exitGameFullscreen(); }, []);

  useEffect(() => {
    void (async () => {
      try { setGames((await listGames()).games); } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'end' });
  }, [sock.chat.length]);

  const lobby = sock.lobby;
  const me = useMemo(() => lobby?.players.find((p) => p.charId === character.charId) ?? null, [lobby, character.charId]);
  const myRoundXp = sock.lastResult?.xp?.[character.charId] ?? null;
  useEffect(() => {
    if (lobby?.state === 'roundEnd' || lobby?.state === 'finalStandings') {
      setXpRefreshKey((k) => k + 1);
      setXpBoost(true);
      const t = window.setTimeout(() => setXpBoost(false), 1800);
      return () => window.clearTimeout(t);
    }
    return;
  }, [lobby?.state]);

  // Phase 4: bump refreshKey ALSO when a new round result arrives
  // (sock.lastResult). Server-side daily challenge progress is updated
  // inside finalizeMatch; this gives the widget + activity feed a
  // second push to refetch the moment the result lands, instead of
  // waiting for the lobby state machine to flip. The widget's existing
  // refreshKey-driven fetch is the auth source — local deltas would
  // need a server-confirm path that doesn't exist yet (deferred to a
  // future store-based rewrite, see customization-updates.md §6).
  useEffect(() => {
    if (sock.lastResult?.matchId) setXpRefreshKey((k) => k + 1);
  }, [sock.lastResult?.matchId]);

  // Analytics: fire match_start once per matchId, match_end once per result.
  useEffect(() => {
    if (!sock.currentMatch?.matchId) return;
    track('match_start', {
      matchId: sock.currentMatch.matchId,
      gameId: sock.currentMatch.gameId,
      players: lobby?.players.filter((p) => !p.isSpectator).length ?? 0,
    });
  }, [sock.currentMatch?.matchId]);
  useEffect(() => {
    if (!sock.lastResult?.matchId) return;
    track('match_end', {
      matchId: sock.lastResult.matchId,
      gameId: sock.lastResult.gameId,
    });
  }, [sock.lastResult?.matchId]);

  // Bump on a reaction send, since react-5 daily challenge ticks per
  // reaction. Server already knows; this just nudges the widget to
  // refetch within ~200ms instead of waiting for the next round end.
  useEffect(() => {
    const r = sock.reactions[sock.reactions.length - 1];
    if (!r) return;
    const t = window.setTimeout(() => setXpRefreshKey((k) => k + 1), 250);
    return () => window.clearTimeout(t);
  }, [sock.reactions.length]);

  // Detect newly-unlocked cosmetics on round/finalStandings transitions.
  useEffect(() => {
    if (lobby?.state !== 'roundEnd' && lobby?.state !== 'finalStandings') return;
    let live = true;
    void (async () => {
      try {
        const s = await getCharacterStats(character.charId);
        if (typeof s.lanterns === 'number') setLanternBalance(s.lanterns);
        setJukeboxOwnedIds(computeOwnedRecordIds(
          new Set(s.achievements.map((a) => a.code)),
          s.inventory as unknown as Record<string, unknown>,
        ));
        const hints = s.inventory.unlockHints ?? [];
        const cur = collectUnlockedIds(hints);
        const prev = prevUnlockedRef.current;
        prevUnlockedRef.current = cur;
        if (!live || !prev) return;
        const newly: UnlockHint[] = [];
        for (const h of hints) {
          if (!h.unlocked) continue;
          const key = `${h.kind}:${h.id}`;
          if (!prev.has(key)) newly.push(h);
        }
        for (const h of newly.slice(0, 6)) {
          toastQueue.push({
            tier: 2,
            rarity: rarityForUnlockKind(h.kind),
            kind: 'cosmetic',
            title: h.label,
            subtitle: cosmeticSubtitle(h.kind),
            rewardChip: cosmeticChip(h.kind),
            icon: cosmeticIcon(h.kind),
          });
        }
        setUnlockedEmotes(s.inventory.emotes ?? []);
      } catch { /* */ }
    })();
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobby?.state, character.charId]);
  useEffect(() => {
    const result = sock.lastResult;
    if (!result?.matchId) return;
    if (lastToastedMatchRef.current === result.matchId) return;
    lastToastedMatchRef.current = result.matchId;

    const xp = result.xp?.[character.charId];
    let legacyTimer: number | null = null;
    if (xp && xp.total > 0) {
      setXpToast({ matchId: result.matchId, xp });
      legacyTimer = window.setTimeout(() => {
        setXpToast((cur) => cur?.matchId === result.matchId ? null : cur);
      }, 4200);
      toastQueue.push({
        tier: 1,
        rarity: 1,
        kind: 'currency',
        title: `+${xp.total} XP`,
        subtitle: xp.items.slice(0, 2).map((it) => `${it.label} +${it.amount}`).join(' · '),
        rewardChip: `Lv ${xp.levelAfter}`,
        icon: '+',
      });
      if (xp.levelAfter > xp.levelBefore) {
        const isMilestone = xp.levelAfter % 5 === 0;
        toastQueue.push({
          tier: isMilestone ? 3 : 2,
          rarity: isMilestone ? 4 : 3,
          kind: 'level',
          title: `Level ${xp.levelAfter}`,
          subtitle: xp.levelAfter - xp.levelBefore > 1
            ? `${xp.levelBefore} → ${xp.levelAfter}`
            : 'Account level up',
          ...(isMilestone ? { rewardChip: 'Milestone' } : {}),
          icon: '⬆',
        });
      }
    }
    const myAwards = (result.awards ?? []).filter((a) => a.charId === character.charId);
    for (const a of myAwards) {
      if (a.label.startsWith('🏅')) {
        const name = a.label.replace(/^🏅\s*/, '').trim();
        toastQueue.push({
          tier: 2,
          rarity: 3,
          kind: 'achievement',
          title: name,
          subtitle: 'Achievement earned',
          rewardChip: 'Badge',
          icon: '★',
        });
      }
    }
    return () => {
      if (legacyTimer !== null) window.clearTimeout(legacyTimer);
    };
  }, [sock.lastResult?.matchId, character.charId]);
  const isHost = !!me?.isHost;
  // "Your Own Tavern" Phase B — jukebox: play the now-playing pick for
  // everyone + drive advancement (host on clip end, any player on the stale
  // fallback). Gated to the waiting room so clips never layer over match music.
  useJukeboxPlayback({
    jukebox: sock.jukebox,
    isHost,
    active: lobby?.state === 'waiting',
    sendJukeboxAdvance: sock.sendJukeboxAdvance,
  });
  // Phase B2 — the brass "now playing" plate + cabinet glow only show in the
  // waiting room (a lobby affordance, same gate as playback above); minimal
  // shape so RoomBackdrop3DScene doesn't need the full JukeboxState/records
  // registry.
  const jukeboxNowPlaying = useMemo(() => {
    if (lobby?.state !== 'waiting' || !sock.jukebox?.playing) return null;
    const p = sock.jukebox.playing;
    return { name: p.name, recordLabel: getRecord(p.recordId)?.label ?? p.recordId };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobby?.state, sock.jukebox?.playing?.charId, sock.jukebox?.playing?.recordId, sock.jukebox?.playing?.startedAt, sock.jukebox?.playing?.name]);
  // "Your Own Tavern" — the HOST's hall decor renders for everyone in the
  // room (owner: "changes to that tavern editor dont show up when you open
  // a lobby"). Re-fetches ONLY when the host identity changes (host
  // migration on leave); a fetch failure just keeps the default hall.
  const hostCharId = lobby?.players.find((p) => p.isHost)?.charId ?? null;
  const [hostTavernConfig, setHostTavernConfig] = useState<TavernConfigDTO>({});
  useEffect(() => {
    if (!hostCharId) { setHostTavernConfig({}); return; }
    let alive = true;
    void (async () => {
      try {
        const host = await getCharacter(hostCharId);
        if (alive) setHostTavernConfig(host.tavern ?? {});
      } catch {
        if (alive) setHostTavernConfig({});
      }
    })();
    return () => { alive = false; };
  }, [hostCharId]);
  const livePlayers = lobby?.players.filter((p) => !p.isSpectator) ?? [];
  const allowedGameIds = lobby?.settings.allowedGames ?? [];
  // R1#17: respect baseGameId when allowedGames stores the base id so
  // separate-variant tiles aren't dropped from the eligibility count.
  // Mirrors PostRoundPane.tsx's HostPickSection predicate.
  const eligibleGameCount = lobby
    ? games.filter((g) =>
        (!allowedGameIds.length
          || allowedGameIds.includes(g.id)
          || (g.baseGameId ? allowedGameIds.includes(g.baseGameId) : false))
        && gameAvailability(g, livePlayers.length).playable,
      ).length
    : 0;
  const readyToStart = !!lobby && livePlayers.length >= 2 && lobby.players.every((p) => p.ready || p.isSpectator);
  const allReady = readyToStart && eligibleGameCount > 0;
  const startBlockedReason = livePlayers.length < 2
    ? 'Need 2+ players'
    : eligibleGameCount === 0
      ? 'No selected games work for this player count'
      : 'Need everyone ready';
  const inviteUrl = useMemo(() => `${window.location.origin}/r/${ticket.roomCode}`, [ticket.roomCode]);

  // ── Walk around the hall (waiting room only) ──
  // Active only in the waiting state with no modal/panel open, so movement keys
  // never fight a dialog and the joystick never overlaps one. Keyboard input is
  // ignored while a text field is focused (handled inside useHallWalk), so the
  // chat composer keeps typing. The server likewise only accepts lobby.move in
  // 'waiting', so this mirrors the authoritative gate.
  const walkEnabled = lobbyState === 'waiting' && !settingsOpen && !profileCharId && !meOpen && !reactionPicker;
  const hallWalk = useHallWalk(walkEnabled);

  useEffect(() => {
    if (openSettingsAfterReset && lobby?.state === 'waiting') {
      setSettingsOpen(true);
      setOpenSettingsAfterReset(false);
    }
  }, [openSettingsAfterReset, lobby?.state]);

  function copy(text: string) {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }).catch(() => {});
  }

  // Connecting timeout — single-shot from mount. If lobby.state hasn't
  // arrived in 12s, force a leave back to home + clear rejoin. Refs are
  // used so the effect runs ONCE on mount (depending on sock.lobby/
  // sock.connected/onLeave caused this to reset the timer every render
  // and never fire). Without this, "host closes lobby + players refresh"
  // could leave players stuck on a black/connecting screen forever.
  const lobbyArrivedRef = useRef(false);
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;
  useEffect(() => {
    if (sock.lobby) lobbyArrivedRef.current = true;
  }, [sock.lobby]);
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!lobbyArrivedRef.current) {
        // eslint-disable-next-line no-console
        console.warn('[Room] connection stalled — falling back to home');
        try { clearRejoin(); } catch { /* */ }
        onLeaveRef.current();
      }
    }, 12_000);
    return () => window.clearTimeout(t);
  }, []);

  // Always-visible emergency leave button. If anything below fails to
  // render (lobby never arrives, host closed the lobby into a weird
  // state, server hiccup), the user can ALWAYS click here to escape
  // the black-screen trap. Floats over everything via fixed position.
  // Fires the same path as the chrome Leave button.
  const escapeButton = (
    <button
      type="button"
      onClick={() => {
        try { clearRejoin(); } catch { /* */ }
        try { sessionStorage.removeItem('tt:nocon-reloads'); } catch { /* */ }
        sock.leave();
        onLeave();
      }}
      style={{
        position: 'fixed',
        top: 8,
        right: 8,
        zIndex: 9999,
        padding: '8px 14px',
        background: 'rgba(20, 8, 10, 0.85)',
        color: '#fff2c8',
        border: '1px solid #ffd770',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
      }}
      aria-label="Leave room"
    >
      Leave
    </button>
  );

  if (sock.kicked) {
    return (
      <>
        {escapeButton}
        <main className="full-center">
          <div className="welcome-card" style={{ textAlign: 'center' }}>
            <h1 className="display" style={{ margin: 0 }}>You were removed.</h1>
            <p className="muted">Reason: {sock.kicked}</p>
            <button type="button" className="btn primary" onClick={onLeave}>Back home</button>
          </div>
        </main>
      </>
    );
  }

  if (sock.finalStandings) {
    return (
      <>
        {escapeButton}
        {/* The set ceremony happens IN the tavern: the same live 3D hall (with
            the table seated as their cosmetic-matched 3D characters) sits behind
            the celebration, replacing the flat page background. The 2D
            <TavernBackdrop> is the WebGL-failure fallback; both are fixed
            z-index:-1 layers behind the ceremony content. contentDim is raised
            for the dense, wide ceremony so the podium/standings/CTA stay crisp. */}
        <TavernBackdrop active={true} />
        <RoomBackdrop3D players={livePlayers} contentDim={0.5} tavernConfig={hostTavernConfig} />
        <FinalStandings
          lobby={lobby!}
          finalStandings={sock.finalStandings}
          isHost={isHost}
          myCharId={character.charId}
          lastRoundXp={myRoundXp}
          xpRefreshKey={xpRefreshKey}
          onNewSession={() => sock.newSession()}
          onChangeSettings={() => {
            setOpenSettingsAfterReset(true);
            sock.newSession();
          }}
          onEnd={() => { sock.leave(); onLeave(); }}
        />
        <ProgressionToast toast={xpToast} />
        <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
      </>
    );
  }

  // Free-mode end-of-session ceremony. Reuses the FinalStandings pane with
  // locally-synthesized standings so the host (and the tally) get a proper
  // winner + standings moment instead of an abrupt kick to home.
  if (freeEnd) {
    return (
      <>
        {escapeButton}
        {/* Free-mode end ceremony — same live 3D tavern hall behind it as the
            ranked finale, with the 2D backdrop as WebGL fallback. */}
        <TavernBackdrop active={true} />
        <RoomBackdrop3D players={livePlayers} contentDim={0.5} tavernConfig={hostTavernConfig} />
        <FinalStandings
          lobby={lobby!}
          finalStandings={freeEnd}
          isHost={isHost}
          myCharId={character.charId}
          lastRoundXp={myRoundXp}
          xpRefreshKey={xpRefreshKey}
          onNewSession={() => { setFreeEnd(null); sock.newSession(); }}
          onChangeSettings={() => { setFreeEnd(null); setOpenSettingsAfterReset(true); sock.newSession(); }}
          onEnd={() => { sock.leave(); onLeave(); }}
        />
        <ProgressionToast toast={xpToast} />
        <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
      </>
    );
  }

  // First-round vote ("the tavern stirs"). Server enters this state when
  // the host hits Start in a vote-mode lobby; resolves to startGame once
  // the timer expires or every live player has voted.
  if (lobby?.state === 'voting') {
    return (
      <>
      {escapeButton}
      <main className="room-screen">
        <TavernBackdrop active={true} />
        <RoomChrome
          lobby={lobby}
          inviteUrl={inviteUrl}
          copied={copied}
          onCopy={() => copy(inviteUrl)}
          onLeave={() => { sock.leave(); onLeave(); }}
          onOpenSettings={() => setSettingsOpen(true)}
          soundOn={soundOn}
          onToggleSound={() => { audio.unlock(); audio.prefs.toggleMute(); setSoundOn(!audio.prefs.get().muted); feedbackPrefs.set({ sound: !audio.prefs.get().muted }); }}
          charId={character.charId}
          xpRefreshKey={xpRefreshKey}
          xpBoost={xpBoost}
          dailyStreak={dailyStreak}
          lanterns={lanternBalance}
        />
        <MatchStartVotePane
          lobby={lobby}
          vote={sock.vote}
          games={games}
          myCharId={character.charId}
          isHost={isHost}
          onVote={sock.voteCast}
          onSkip={sock.skipRoundEnd}
          onInspectPlayer={setProfileCharId}
        />
        <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
        <SettingsPanel
          open={settingsOpen}
          isHost={isHost}
          settings={lobby.settings}
          games={games}
          playerCount={lobby.players.filter((p) => !p.isSpectator).length}
          onClose={() => setSettingsOpen(false)}
          onChange={sock.setSettings}
        />
      </main>
      </>
    );
  }

  // Game in progress
  if (lobby?.state === 'playing' && sock.currentMatch) {
    const activeGame = games.find((g) => g.id === sock.currentMatch!.gameId) ?? games.find((g) => g.baseGameId === sock.currentMatch!.gameId);
    return (
      <>
      <main className="play-screen">
        {/* §1b in-play chrome, consolidated into ONE reserved top bar so it
            never overlaps the game's own in-canvas score/player-names HUD
            (the "score stuck behind the Leave button / game name / mode"
            bug). The bar owns a fixed top band; `.game-stage-frame` starts
            BELOW it (see --tt-hud-band in global.css), so the canvas + any
            game-drawn HUD can no longer share pixels with this chrome across
            desktop / mobile portrait / landscape. Game name + mode pin left,
            Leave pins right — reserved corners that can't collide. During
            play the lobby has arrived, so the always-on black-screen escape
            button isn't needed here; this Leave fires the same teardown. */}
        <div className="play-hud-bar">
          <div className="play-hud-left">
            {!fsActive && !isIos() && fullscreenSupported() && (
              <button
                type="button"
                className="fs-pill"
                onClick={() => void enterGameFullscreen(gameOrientation(sock.currentMatch!.gameId))}
                aria-label="Re-enter fullscreen"
                title="Fullscreen"
              >
                ⛶
              </button>
            )}
            <span className="play-hud-game">{activeGame?.name ?? gameName(sock.currentMatch.gameId) ?? ''}</span>
            <EventModeBadge mode={lobby.settings.eventMode} />
          </div>
          <button
            type="button"
            className="play-hud-leave"
            onClick={() => {
              try { clearRejoin(); } catch { /* */ }
              try { sessionStorage.removeItem('tt:nocon-reloads'); } catch { /* */ }
              sock.leave();
              onLeave();
            }}
            aria-label="Leave game"
            title="Leave"
          >
            <LogOut size={15} /><span>Leave</span>
          </button>
        </div>
        {/* Tutorial banner removed (2026-05-12): UniversalMatchIntro already
            shows headline + tip during the pre-round phase; the persistent
            in-game card was covering game canvas info. */}
        {/* Wooden table-edge frame around the canvas — per docs/ui/room-screens.md §10 (GS1). */}
        <div className="game-stage-frame">
          <GameStage
            key={sock.currentMatch.matchId}
            matchId={sock.currentMatch.matchId}
            gameId={sock.currentMatch.gameId}
            startsAt={sock.currentMatch.startsAt}
            endsAt={sock.currentMatch.endsAt}
            params={sock.currentMatch.params}
            mySlot={me?.slot ?? 0}
            players={lobby.players.filter((p) => !p.isSpectator).map((p) => ({ charId: p.charId, name: p.name, icon: p.icon, slot: p.slot, connId: '' }))}
            tickPayload={sock.lastTick?.matchId === sock.currentMatch.matchId ? sock.lastTick.payload : undefined}
            result={sock.lastResult}
            sendInput={sock.sendInput}
            gameMeta={activeGame}
            eventMode={lobby.settings.eventMode}
            {...(activeGame?.inputBindings
              ? { inputBindings: activeGame.inputBindings }
              : {})}
          />
        </div>
        <UniversalMatchIntro
          key={sock.currentMatch.matchId}
          matchId={sock.currentMatch.matchId}
          game={activeGame}
          fallbackGameId={sock.currentMatch.gameId}
          players={lobby.players.filter((p) => !p.isSpectator)}
          startsAt={sock.currentMatch.startsAt}
          countdownSec={lobby.settings.roundCountdownSec}
          isFirstRound={true}
          mySlot={me?.slot ?? 0}
        />
        {/* Own Tavern §3: avatars walk out through the glowing tavern door and
            the camera follows them into the game world, replacing the hard cut.
            Runs INSIDE the intro/countdown window (no added wall-clock), above
            the booting GameStage + intro overlay, then cross-fades to reveal
            them. Clears itself the moment it (or its fallback) completes. */}
        {doorWalk && doorWalk.matchId !== doorWalkDoneMatch && (
          <DoorWalkTransition
            key="door-walk"
            matchId={doorWalk.matchId}
            players={lobby.players.filter((p) => !p.isSpectator)}
            meCharId={character.charId}
            startsAt={doorWalk.startsAt}
            tavernConfig={hostTavernConfig}
            onDone={() => setDoorWalkDoneMatch(doorWalk.matchId)}
          />
        )}
        <RotateOverlay gameId={sock.currentMatch.gameId} matchId={sock.currentMatch.matchId} />
        <ReactionsOverlay reactions={sock.reactions} />
        {!isTouchDevice && (
          <div className="play-footer">
            <ReactionPicker open={reactionPicker} unlocked={unlockedEmotes} onPick={(e) => { sock.sendReaction(e); setReactionPicker(false); }} onToggle={() => setReactionPicker((v) => !v)} />
          </div>
        )}
      </main>
      </>
    );
  }

  if (lobby?.state === 'roundEnd') {
    return (
      <>
      {escapeButton}
      <main className="room-screen">
        <TavernBackdrop active={true} />
        {/* Between-rounds pane lives IN the 3D tavern too — the same seated
            hall the waiting room uses, so the round wrap-up happens in the
            world instead of over a flat page. Denser than the waiting room, so
            the centre scrim is raised a touch to keep the recap/standings crisp.
            Falls back to the 2D <TavernBackdrop> above on any WebGL failure. */}
        <RoomBackdrop3D players={livePlayers} contentDim={0.4} tavernConfig={hostTavernConfig} />
        <RoomChrome
          lobby={lobby}
          inviteUrl={inviteUrl}
          copied={copied}
          onCopy={() => copy(inviteUrl)}
          onLeave={() => { sock.leave(); onLeave(); }}
          onOpenSettings={() => setSettingsOpen(true)}
          soundOn={soundOn}
          onToggleSound={() => { audio.unlock(); audio.prefs.toggleMute(); setSoundOn(!audio.prefs.get().muted); feedbackPrefs.set({ sound: !audio.prefs.get().muted }); }}
          charId={character.charId}
          xpRefreshKey={xpRefreshKey}
          xpBoost={xpBoost}
          dailyStreak={dailyStreak}
          lanterns={lanternBalance}
        />
        <PostRoundPane
          lobby={lobby}
          result={sock.lastResult}
          vote={sock.vote}
          games={games}
          myCharId={character.charId}
          isHost={isHost}
          onVote={sock.voteCast}
          onPickHost={(id) => sock.freeLaunch(id) /* same handler also valid for roundEnd host pick */}
          onSkip={sock.skipRoundEnd}
          onInspectPlayer={setProfileCharId}
        />
        <ProgressionToast toast={xpToast} />
        <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
        <SettingsPanel
          open={settingsOpen}
          isHost={isHost}
          settings={lobby.settings}
          games={games}
          playerCount={lobby.players.filter((p) => !p.isSpectator).length}
          onClose={() => setSettingsOpen(false)}
          onChange={sock.setSettings}
        />
      </main>
      </>
    );
  }

  // Default: waiting
  return (
    <>
    {/* The always-on escape hatch exists for the "lobby never arrived"
        black-screen trap. Once the lobby is live, RoomChrome carries Leave
        — showing both stacked two Leave buttons on mobile (owner shot). */}
    {!lobby && escapeButton}
    <main className="room-screen">
      <TavernBackdrop active={true} />
      {/* WS-4: live 3D tavern-bar backdrop with the seated players as their
          cosmetic-matched 3D characters. Sits behind the room UI (covers the
          2D SVG above only when WebGL works); guarded so a WebGL/chunk failure
          falls back to the SVG and never black-screens the room.
          Walk-around: the local player's figure is drivable (WASD / joystick)
          and pushes other players — synced low-rate over the lobby WS. */}
      {/* Reverse door-walk: if we just returned to the tavern from a match,
          the others file back in through the doorway (entryWalk, read once at
          mount). */}
      <RoomBackdrop3D
        players={livePlayers}
        meCharId={character.charId}
        entryWalk={prevLobbyStateRef.current === 'finalStandings' || prevLobbyStateRef.current === 'playing' || prevLobbyStateRef.current === 'roundEnd'}
        inputRef={hallWalk.inputRef}
        onLocalMove={sock.sendMove}
        subscribeMoves={sock.subscribeMoves}
        sendToyUse={sock.sendToyUse}
        subscribeToys={sock.subscribeToys}
        nameplates
        onInspectPlayer={setProfileCharId}
        chat={sock.chat}
        reactions={sock.reactions}
        dimPlates={!!sock.vote || settingsOpen || profileCharId !== null}
        tavernConfig={hostTavernConfig}
        jukeboxNowPlaying={jukeboxNowPlaying}
      />
      {hallWalk.joystick}
      {/* NPE-1: first-visit walk/vote hints — non-blocking, once ever. */}
      {lobby && <FirstVisitHints />}
      {/* First-time-host nudge toward Table Rules — non-blocking, once ever
          per browser (owner: "hard to even know you have to click settings
          in lobby to go to lobby settings"). */}
      {lobby && <FirstHostHint isHost={isHost} onOpenSettings={() => setSettingsOpen(true)} />}
      <RoomChrome
        lobby={lobby}
        inviteUrl={inviteUrl}
        copied={copied}
        onCopy={() => copy(inviteUrl)}
        onLeave={() => { sock.leave(); onLeave(); }}
        onOpenSettings={() => setSettingsOpen(true)}
        soundOn={soundOn}
        onToggleSound={() => { audio.unlock(); audio.prefs.toggleMute(); setSoundOn(!audio.prefs.get().muted); feedbackPrefs.set({ sound: !audio.prefs.get().muted }); }}
        charId={character.charId}
        xpRefreshKey={xpRefreshKey}
        xpBoost={xpBoost}
        dailyStreak={dailyStreak}
        lanterns={lanternBalance}
      />

      {/* One-time iOS PWA nudge — waiting room only, never during play.
          iPhone Safari has no Fullscreen API; Add-to-Home-Screen is the
          only path to edge-to-edge play there (§1b). */}
      <IosInstallNudge />

      {lobby && (
        <>
          {/* 8P Phase 1: the seat-card grid is GONE. The walkable hall +
              nameplates above heads (RoomBackdrop3D nameplates prop) is the
              roster; this slim drawer is its screen-reader surface, the
              WebGL-failure fallback, and the reliable home of profile/kick.
              Card seat-cosmetics render in-world now: border → nameplate
              frame, background+hue → aura disc, particle → walk trail. */}
          <RosterDrawer
            players={lobby.players}
            maxPlayers={lobby.settings.maxPlayers}
            meCharId={character.charId}
            isHost={isHost}
            onInspect={setProfileCharId}
            onKick={(id) => sock.kick(id)}
          />
          {/* Waiting Room 2.0 — THE HALL IS THE SCREEN. All chrome hugs
              the edges; the center stays clear so players actually see
              each other walking. The chalkboard is a one-line status pill
              (tap → full board sheet); the verbs live in one bottom bar. */}
          <button
            type="button"
            className="room-status-pill"
            onClick={() => setBoardOpen((v) => !v)}
            aria-expanded={boardOpen}
            aria-label="Table status — tap for details"
          >
            <span aria-hidden="true">🍺</span>
            <span>{lobby.players.filter((p) => p.ready || p.isSpectator).length}/{lobby.players.length} ready</span>
            <span className="room-status-pill-dot" aria-hidden="true">·</span>
            <span>{lobby.settings.mode === 'free' ? 'Free play' : `Round ${lobby.setRoundsPlayed}/${lobby.settings.roundsPerSet}`}</span>
          </button>
          {boardOpen && (
            <div className="room-board-backdrop" onClick={() => setBoardOpen(false)}>
              <section
                className="table-chalkboard room-board-sheet"
                aria-live="polite"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="table-chalkboard-rope table-chalkboard-rope-l" aria-hidden="true" />
                <span className="table-chalkboard-rope table-chalkboard-rope-r" aria-hidden="true" />
                <div className="table-chalkboard-slate">
                  <div className="table-chalkboard-line">
                    <span className="table-chalkboard-label">Tonight's set:</span>
                    <strong>{lobby.settings.mode === 'free' ? 'Free play' : `${lobby.setRoundsPlayed} of ${lobby.settings.roundsPerSet} rounds`}</strong>
                  </div>
                  <div className="table-chalkboard-line">
                    <span className="table-chalkboard-label">Players ready:</span>
                    <strong>{lobby.players.filter((p) => p.ready || p.isSpectator).length}/{lobby.players.length}</strong>
                  </div>
                  <div className="table-chalkboard-line">
                    <EventModeBadge mode={lobby.settings.eventMode} />
                  </div>
                  {/* Discoverability fix: the chalkboard is where players
                      already look for table state, so the door to Table
                      Rules lives here too, not just the header button. Host
                      gets an edit affordance; everyone else can still tap
                      through to VIEW the current rules (SettingsPanel
                      disables every field when !isHost). */}
                  <div className="table-chalkboard-line table-chalkboard-line-rules">
                    <span className="table-chalkboard-label">Table rules:</span>
                    <button type="button" className="table-chalkboard-rules-link" onClick={() => { setBoardOpen(false); setSettingsOpen(true); }}>
                      <ScrollText size={13} aria-hidden="true" />
                      {isHost ? 'Edit the rules' : 'View the rules'}
                    </button>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* ONE bottom action bar: chat (thumb-reachable), the ready
              tankard, and the host bell — compact, floating, no slab. */}
          <section className="room-action-bar">
            <TavernFloor
              players={lobby.players.filter((p) => !p.isSpectator)}
              onInspectPlayer={setProfileCharId}
              chat={sock.chat}
              reactions={sock.reactions}
              bubblesInWorld
              compact
              {...(lobby.settings.chatEnabled ? { onSendChat: sock.sendChat } : {})}
              {...(lobby.settings.reactionsEnabled ? { onSendReaction: sock.sendReaction } : {})}
              reactionEmojis={[...REACTION_PICKER_BASE, ...unlockedEmotes.filter((e) => REACTION_PICKER_UNLOCKABLE.includes(e))]}
              chatEnabled={lobby.settings.chatEnabled}
              meCharId={character.charId}
            />
            <button
              type="button"
              className={`tavern-chat-fab tavern-jukebox-fab${jukeboxNowPlaying ? ' is-playing' : ''}`}
              onClick={() => setJukeboxOpen(true)}
              aria-label="Open the jukebox"
              title={jukeboxNowPlaying ? `${jukeboxNowPlaying.name}'s pick — ${jukeboxNowPlaying.recordLabel}` : 'Jukebox'}
            >
              <Music size={17} aria-hidden="true" />
              {/* Portrait users can't always see the world plate (the jukebox
                  anchor sits near the frame edge at some aspects) — the chip
                  itself carries whose song is playing so it's never lost. */}
              {jukeboxNowPlaying && (
                <span className="tavern-jukebox-fab-label">
                  {jukeboxNowPlaying.name.length > 10 ? `${jukeboxNowPlaying.name.slice(0, 9)}…` : jukeboxNowPlaying.name}'s pick
                </span>
              )}
            </button>
            {lobby.settings.mode !== 'free' && (
              <>
                <button
                  type="button"
                  className={`tavern-action tavern-action-ready compact${me?.ready ? ' is-ready' : ''}`}
                  disabled={!me}
                  onClick={() => {
                    // §1b: fullscreen MUST be requested inside the user
                    // gesture. Enter when raising the tankard (going ready),
                    // not when backing out.
                    if (!me?.ready) void enterGameFullscreen();
                    sock.ready(!me?.ready);
                  }}
                  aria-label={me?.ready ? 'Mark not ready' : 'Mark ready'}
                >
                  <span className="tankard-body" aria-hidden="true">
                    <span className="tankard-fill" />
                    <span className="tankard-foam" />
                    <span className="tankard-band tankard-band-top" />
                    <span className="tankard-band tankard-band-mid" />
                    <span className="tankard-handle" />
                  </span>
                  <span className="tavern-action-text">
                    <span className="tavern-action-title">
                      {me?.ready ? 'Not ready' : 'Ready'}
                    </span>
                    <span className="tavern-action-hint">
                      {me?.ready ? 'tap to set the tankard down' : 'raise your tankard'}
                    </span>
                  </span>
                </button>
                {isHost && (
                  <button
                    type="button"
                    className={`tavern-action tavern-action-start compact${allReady ? ' is-armed' : ''}`}
                    onClick={() => {
                      // §1b: same gesture rule — ring the bell, take the screen.
                      void enterGameFullscreen();
                      sock.start();
                    }}
                    disabled={!allReady}
                    title={allReady ? 'Start the round' : startBlockedReason}
                    aria-label="Start the round"
                  >
                    <span className="bell-rig" aria-hidden="true">
                      <span className="bell-rope" />
                      <span className="bell-body" />
                      <span className="bell-clapper" />
                    </span>
                    <span className="tavern-action-text">
                      <span className="tavern-action-title">Start</span>
                      <span className="tavern-action-hint">
                        {allReady ? 'ring the bell to begin' : startBlockedReason}
                      </span>
                    </span>
                  </button>
                )}
              </>
            )}
          </section>

          {jukeboxOpen && (
            <JukeboxDrawer
              jukebox={sock.jukebox}
              myCharId={character.charId}
              ownedIds={jukeboxOwnedIds}
              onRequest={(id) => sock.sendJukeboxRequest(id)}
              onClose={() => setJukeboxOpen(false)}
            />
          )}

          {/* Side stack: daily challenge + errors — top-right, out of the
              hall's way. */}
          <div className="room-side-stack">
            <CollapsibleDailyChallenge>
              <DailyChallengeWidget charId={character.charId} refreshKey={xpRefreshKey} compact />
            </CollapsibleDailyChallenge>
            {sock.error && <div role="alert" className="gate-error">{sock.error}</div>}
          </div>

          {lobby.settings.mode === 'free' && (
            <>
              <FreeModeStandings
                lobby={lobby}
                result={sock.lastResult}
                games={games}
                myCharId={character.charId}
              />
              <section className="room-controls">
                {isHost && (
                  <button type="button" className="btn ghost big" onClick={() => setFreeEnd(buildFreeSummary(lobby))}>End session</button>
                )}
              </section>
              <FreeModeGrid lobby={lobby} games={games} onLaunch={sock.freeLaunch} />
            </>
          )}

          {/* Old standalone chat box removed — chat is now integrated
              into TavernFloor via speech bubbles + composer pill.
              Per docs/ui/tavern-floor-chat.md §3.5. */}
        </>
      )}

      <SettingsPanel
        open={settingsOpen}
        isHost={isHost}
        settings={lobby?.settings ?? sockDefaults()}
        games={games}
        playerCount={lobby?.players.filter((p) => !p.isSpectator).length ?? 0}
        onClose={() => setSettingsOpen(false)}
        onChange={sock.setSettings}
      />
      <ProgressionToast toast={xpToast} />
      <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
      <MeDrawer open={meOpen} onClose={() => setMeOpen(false)} />
      <CharacterProfileModal
        charId={profileCharId}
        games={games}
        editable={profileCharId === character.charId}
        viewerCharId={character.charId}
        onClose={() => setProfileCharId(null)}
        onUpdated={(c) => { onCharacterUpdated(c); sock.refreshPresence(); }}
      />
    </main>
    </>
  );
}

/** Synthesize a FinalStandings-shaped summary from a free-mode lobby's
 *  accumulated lobbyScores, so ending a free session shows a proper
 *  winner + standings ceremony instead of an abrupt kick. Mirrors the
 *  server's emitFinalStandings tie handling (ties at the top = no winner). */
function buildFreeSummary(lobby: import('@tt/lobby').LobbyView): {
  standings: Array<{ charId: string; name: string; points: number }>;
  awards: Array<{ charId: string; label: string }>;
  winnerHue: number;
} {
  const players = lobby.players.filter((p) => !p.isSpectator);
  const standings = players
    .map((p) => ({ charId: p.charId, name: p.name, points: p.lobbyScore }))
    .sort((a, b) => b.points - a.points || a.charId.localeCompare(b.charId));
  const winner = standings[0];
  const tied = !!winner && winner.points > 0
    && standings.filter((s) => s.points === winner.points).length > 1;
  const awards: Array<{ charId: string; label: string }> = [];
  if (winner && winner.points > 0 && !tied) {
    awards.push({ charId: winner.charId, label: 'Free play champion' });
  }
  const winnerHue = winner
    ? (players.find((p) => p.charId === winner.charId)?.icon.hue ?? 30)
    : 30;
  return { standings, awards, winnerHue };
}

function sockDefaults() {
  return {
    mode: 'vote' as const,
    eventMode: 'standard' as const,
    roundsPerSet: 5,
    winCondition: 'rounds' as const,
    targetScore: 1000,
    maxPlayers: 8 as const,
    allowedGames: [] as string[],
    voteOptions: 3,
    voteTimerSec: 15,
    roundCountdownSec: 3,
    pickWho: 'vote' as const,
    hostRotation: 'fixed' as const,
    latePlayers: 'join' as const,
    chatEnabled: true,
    reactionsEnabled: true,
    pointMultiplier: 1 as const,
    lobbyPersistHours: 4,
    quickRejoinSec: 30,
    showTutorials: true,
  };
}

function EventModeBadge({ mode }: { mode?: import('@tt/lobby').LobbySettings['eventMode'] }) {
  const label = {
    standard: 'Standard Night',
    lightning: 'Lightning Night',
    comeback: 'Comeback Night',
    chaos: 'Chaos Night',
    cozy: 'Cozy Night',
  }[mode ?? 'standard'];
  return <span className={`event-mode-badge mode-${mode ?? 'standard'}`}>{label}</span>;
}

function RoomChrome({
  lobby, inviteUrl, copied, onCopy, onLeave, onOpenSettings, soundOn, onToggleSound,
  charId, xpRefreshKey, xpBoost, dailyStreak, lanterns,
}: {
  lobby: import('@tt/lobby').LobbyView | null;
  inviteUrl: string;
  copied: boolean;
  onCopy: () => void;
  onLeave: () => void;
  onOpenSettings: () => void;
  soundOn: boolean;
  onToggleSound: () => void;
  charId: string;
  xpRefreshKey: number;
  xpBoost: boolean;
  dailyStreak: number;
  /** Phase D — lantern balance pip. Sourced from /api/whoami via the
   *  cached Character; updates on round end (via stats refresh) and on
   *  return from /payment-success (via App.tsx onCharacterRefresh). */
  lanterns?: number;
}) {
  const liveCount = lobby ? lobby.players.filter((p) => !p.isSpectator).length : 0;
  // Defensive fallback to the platform ceiling: a lobby row written before
  // this setting existed (or a client racing ahead of a backend deploy that
  // hasn't shipped it yet) sends `settings.maxPlayers` as undefined — show
  // the old fixed cap rather than a blank number.
  const tableCap = lobby?.settings.maxPlayers ?? MAX_PLAYERS;
  // Owner (2026-07-06): "the banner showcasing all the lobby info still
  // covers up so much of the room, maybe it can be minimizable?" — same
  // persistence pattern as CollapsibleDailyChallenge. Minimized = a slim
  // pill with just the code + count + expand; the HALL is the screen.
  const [minimized, setMinimized] = useState(() => {
    try { return localStorage.getItem('tt:roomHeaderMin') === '1'; } catch { return false; }
  });
  const toggleMin = () => setMinimized((m) => {
    try { localStorage.setItem('tt:roomHeaderMin', m ? '0' : '1'); } catch { /* private mode */ }
    return !m;
  });
  if (minimized) {
    return (
      <header className="room-header is-min">
        <button type="button" className="room-header-min-pill" onClick={toggleMin} aria-label="expand room info" title="Show room info">
          <span className="display room-title-min">{lobby?.roomCode ?? '…'}</span>
          <span className="muted">{liveCount}/{tableCap}</span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      </header>
    );
  }
  return (
    <header className="room-header">
      <div className="room-identity">
        <h1 className="display room-title">{lobby?.roomCode ?? '…'}</h1>
        <p className="room-status muted">
          {lobby ? (
            <>
              <span>{liveCount}/{tableCap} at the table</span>
              <span className="room-mode-badge">{lobby.settings.mode}</span>
            </>
          ) : 'connecting…'}
        </p>
      </div>
      <div className="room-stats-bar">
        <XPBar charId={charId} refreshKey={xpRefreshKey} boost={xpBoost} />
        {dailyStreak > 0 && (
          <span className="streak-ribbon" title={`${dailyStreak}-day streak`}>
            <Flame size={14} />
            <span>{dailyStreak}</span>
          </span>
        )}
        {typeof lanterns === 'number' && (
          <span className="room-lantern-pip" title={`${lanterns.toLocaleString()} lanterns`} aria-label={`${lanterns} lanterns`}>
            <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>🏮</span>
            <span>{lanterns.toLocaleString()}</span>
          </span>
        )}
        <FeaturedChip />
        <BellButton charId={charId} refreshKey={xpRefreshKey} />
      </div>
      <div className="room-actions">
        <button type="button" className="btn ghost icon-btn" onClick={onCopy} aria-label="copy invite link" title="Invite link">
          <Link2 size={16} /><span>{copied ? 'Copied!' : 'Invite'}</span>
        </button>
        <button type="button" className="btn ghost icon-btn" onClick={onOpenSettings} aria-label="table rules — lobby settings" title="Table Rules (lobby settings)">
          <ScrollText size={16} /><span>Table Rules</span>
        </button>
        <button type="button" className="btn ghost icon-btn" onClick={onToggleSound} aria-label="toggle sound" title={soundOn ? 'Mute' : 'Unmute'}>
          {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}<span>Sound</span>
        </button>
        <button type="button" className="btn ghost icon-btn leave" onClick={onLeave} aria-label="leave room" title="Leave">
          <LogOut size={16} /><span>Leave</span>
        </button>
        <button type="button" className="btn ghost icon-btn room-header-min-btn" onClick={toggleMin} aria-label="minimize room info" title="Minimize — see the hall">
          <ChevronUp size={16} />
        </button>
      </div>
      <small style={{ display: 'none' }}>{inviteUrl}</small>
    </header>
  );
}

function CollapsibleDailyChallenge({ children }: { children: React.ReactNode }) {
  // Default-collapsed so the daily challenge widget no longer eats vertical
  // space above Ready / Start. Persisted per-session in localStorage.
  const KEY = 'tt:room:dailyExpanded';
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(KEY, open ? '1' : '0'); } catch { /* */ }
  }, [open]);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      style={{ margin: '8px auto 0', maxWidth: 480, textAlign: 'center' }}
    >
      <summary style={{ cursor: 'pointer', color: 'var(--c-muted, #b09a72)', fontSize: 12, listStyle: 'none', padding: '4px 8px' }}>
        ▸ Daily challenge
      </summary>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>{children}</div>
    </details>
  );
}

function FeaturedChip() {
  const featured = featuredGameForWeek();
  return (
    <span className="featured-chip" title={`This week's featured game (${Math.round(FEATURED_XP_MULTIPLIER * 100)}% Season XP)`}>
      <Star size={14} />
      <span>Featured: {prettifyGameId(featured.gameId)}</span>
    </span>
  );
}
function prettifyGameId(id: string): string {
  return id.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}

function ReactionsOverlay({ reactions }: { reactions: Array<{ id: number; emoji: string }> }) {
  return (
    <div className="reactions-overlay" aria-hidden="true">
      {reactions.map((r, i) => (
        <span key={r.id} className="reaction-pop" style={{ left: `${(i * 13) % 80 + 10}%` }}>{r.emoji}</span>
      ))}
    </div>
  );
}

function ProgressionToast({ toast }: { toast: { matchId: string; xp: RoundXp } | null }) {
  if (!toast) return null;
  const leveled = toast.xp.levelAfter > toast.xp.levelBefore;
  return (
    <div className="toast-stack progression-toast-stack" aria-live="polite">
      <div className="toast ember progression-toast">
        <span className="progression-toast-kicker">{leveled ? 'Level up!' : 'XP earned'}</span>
        <strong>+{toast.xp.total} XP</strong>
        <span>Lv {toast.xp.levelAfter} · {toast.xp.toNext.current}/{toast.xp.toNext.needed}</span>
      </div>
    </div>
  );
}

function ReactionPicker({ open, onPick, onToggle, unlocked }: { open: boolean; onPick: (e: string) => void; onToggle: () => void; unlocked: string[] }) {
  return (
    <div className="reaction-picker">
      <button type="button" className="btn ghost icon-btn" onClick={onToggle} aria-label="reactions" title="Send a reaction">
        <Smile size={16} />
      </button>
      {open && (
        <div className="reaction-menu" role="menu">
          {REACTION_PICKER_BASE.map((e) => (
            <button key={e} type="button" className="reaction-btn" onClick={() => onPick(e)}>{e}</button>
          ))}
          {REACTION_PICKER_UNLOCKABLE.map((e) => {
            const open = unlocked.includes(e);
            return (
              <button
                key={e}
                type="button"
                className={`reaction-btn${open ? '' : ' locked'}`}
                onClick={() => open && onPick(e)}
                disabled={!open}
                title={open ? '' : 'Locked — earn it through play'}
              >
                {open ? e : '🔒'}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type UnlockHint = NonNullable<import('../../lib/api').CosmeticInventory['unlockHints']>[number];

function collectUnlockedIds(hints: UnlockHint[]): Set<string> {
  const out = new Set<string>();
  for (const h of hints) {
    if (h.unlocked) out.add(`${h.kind}:${h.id}`);
  }
  return out;
}

function rarityForUnlockKind(kind: UnlockHint['kind']): 1 | 2 | 3 | 4 {
  switch (kind) {
    case 'title': return 3;
    case 'seatBorder':
    case 'seatBackground':
    case 'seatParticle': return 3;
    case 'hat':
    case 'body':
    case 'face': return 2;
    default: return 2;
  }
}
function cosmeticSubtitle(kind: UnlockHint['kind']): string {
  switch (kind) {
    case 'title': return 'New title unlocked';
    case 'seatBorder': return 'New seat frame unlocked';
    case 'seatBackground': return 'New seat backdrop unlocked';
    case 'seatParticle': return 'New seat particles unlocked';
    case 'hat': return 'New hat unlocked';
    case 'body': return 'New body unlocked';
    case 'face': return 'New face unlocked';
    case 'hue':
    case 'accentHue': return 'New color unlocked';
    case 'emote': return 'New emote unlocked';
    default: return 'Unlocked';
  }
}
function cosmeticChip(kind: UnlockHint['kind']): string {
  switch (kind) {
    case 'title': return 'Title';
    case 'seatBorder': return 'Frame';
    case 'seatBackground': return 'Backdrop';
    case 'seatParticle': return 'Particles';
    case 'hat': return 'Hat';
    case 'body': return 'Body';
    case 'face': return 'Face';
    case 'hue':
    case 'accentHue': return 'Color';
    case 'emote': return 'Emote';
    default: return 'Cosmetic';
  }
}
function cosmeticIcon(kind: UnlockHint['kind']): string {
  switch (kind) {
    case 'title': return '🏷';
    case 'seatBorder': return '◇';
    case 'seatBackground': return '◯';
    case 'seatParticle': return '✶';
    case 'hat': return '🎩';
    case 'body': return '👤';
    case 'face': return '☻';
    case 'hue':
    case 'accentHue': return '◐';
    case 'emote': return '🎭';
    default: return '✦';
  }
}
